import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { SERVICE_LIST, personalizeServices } from '../data/kmsServices';
import { MandiPricesPanel, TendersPanel } from '../components/GovtDataPanels';
import { BenefitBanner, RecommendedStrip } from '../components/ForYouPersonalized';
import BenefitCalculators from '../components/BenefitCalculators';
import { AlertTriangle, ChevronRight, Sparkles } from 'lucide-react';
import useSeo from '../hooks/useSeo';

const ACCENT_BG = {
  emerald: 'from-emerald-50  to-emerald-100/40 ring-emerald-200/60',
  sky:     'from-sky-50      to-sky-100/40     ring-sky-200/60',
  amber:   'from-amber-50    to-amber-100/40   ring-amber-200/60',
  rose:    'from-rose-50     to-rose-100/40    ring-rose-200/60',
  green:   'from-green-50    to-green-100/40   ring-green-200/60',
  indigo:  'from-indigo-50   to-indigo-100/40  ring-indigo-200/60',
  violet:  'from-violet-50   to-violet-100/40  ring-violet-200/60',
};

const ACCENT_TEXT = {
  emerald: 'text-emerald-700',
  sky:     'text-sky-700',
  amber:   'text-amber-700',
  rose:    'text-rose-700',
  green:   'text-green-700',
  indigo:  'text-indigo-700',
  violet:  'text-violet-700',
};

const KmsServices = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recommended, ordered } = personalizeServices(user);

  useSeo({
    title: 'For You — Loans, Insurance, Health, Skills, Business & more | KnowMySchemes',
    description: 'Everything you need in one place — compare loans, insurance, savings, healthcare, agri inputs, skill courses and business tools. Government options first, trusted private partners alongside.',
    canonical: 'https://knowmyschemes.com/money-hub',
    noindex: false,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'For You — KMS Services',
      itemListElement: SERVICE_LIST.map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: s.title,
        url: `https://knowmyschemes.com/money-hub/${s.slug}`,
      })),
    },
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-white">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="kms-services-page">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">
            <Sparkles className="w-4 h-4" /> KMS Services
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            Everything you need <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">in one place.</span>
          </h1>
          <p className="mt-3 text-base text-gray-600 max-w-2xl">
            We connect you to trusted partners. <strong>We never handle your money.</strong> Government schemes appear first, private partners alongside — pick what works for you.
          </p>
        </div>

        {/* Personalised benefit banner (Feature b) */}
        <BenefitBanner user={user} />

        {/* Recommended for you (Feature a) — occupation-based */}
        <RecommendedStrip recommended={recommended} />

        {/* All services — reordered by profile */}
        <div className="mb-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            {recommended.length > 0 ? 'All services' : 'Explore services'}
          </h2>
        </div>
        {/* 7 cards — compact grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="services-grid">
          {ordered.map((s) => (
            <button
              key={s.slug}
              onClick={() => navigate(`/money-hub/${s.slug}`)}
              data-testid={`service-card-${s.slug}`}
              className={`group relative text-left rounded-xl bg-gradient-to-br ${ACCENT_BG[s.accent]} ring-1 p-4 transition hover:shadow-lg hover:-translate-y-0.5`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-2xl leading-none">{s.icon}</div>
                <ChevronRight className={`w-4 h-4 ${ACCENT_TEXT[s.accent]} opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition`} />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-gray-900 leading-tight">{s.title}</h3>
              <p className="mt-1 text-[11px] text-gray-600 leading-relaxed line-clamp-2">{s.subtitle}</p>
              <p className={`mt-2.5 text-[10px] font-semibold uppercase tracking-wide ${ACCENT_TEXT[s.accent]} inline-flex items-center gap-0.5`}>
                Explore <ChevronRight className="w-3 h-3" />
              </p>
            </button>
          ))}
        </div>

        {/* Global disclaimer */}
        <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="services-global-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>KnowMySchemes Services</strong> connects you to trusted third-party platforms. We provide information only and do not handle payments, investments, or medical services. Always read terms and conditions on partner websites before proceeding.
          </div>
        </div>

        {/* Quick calculators (EMI / cover / FD) */}
        <BenefitCalculators />

        {/* Live Government Data — AP/TG/KA */}
        <section className="mt-12" data-testid="live-data-section">
          <div className="mb-5">
            <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">
              <Sparkles className="w-4 h-4" /> Live Government Data
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
              Fresh from <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">official sources</span>
            </h2>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Today's mandi prices, active government tenders, and district-level scholarship data — for Andhra Pradesh, Telangana and Karnataka.
            </p>
          </div>

          <div className="space-y-4">
            <MandiPricesPanel defaultState={user?.state} />
            <TendersPanel defaultState={user?.state} />
          </div>
        </section>

        <p className="mt-6 text-xs text-gray-400">
          {user ? `Signed in as ${user.name}.` : 'Browsing as guest.'} Government schemes are highlighted in green; private partners are clearly labelled inside each service.
        </p>
      </main>
    </div>
  );
};

export default KmsServices;
